import useAudioStore from '@/shared/store/audio.store'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url'
import { useState, useCallback } from 'react'
import type { ComponentProps, FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import Button from './Button'
import Icon from './Icon'
import useMediaStore from '@/shared/store/media.store.tsx'

type PlayFromUrlProps = { soundId: string; url: string; playAt?: number }
type PlayFromBufferProps = {
  soundId: string
  buffer: AudioBuffer
  playAt?: number
}
type PlayFromStoreProps = { soundId: string; playAt?: number }
type PlayFromFetchableProps = {
  soundId: string
  fetchBuffer: () => Promise<AudioBuffer>
  playAt?: number
}

type PlayButtonProps = ComponentProps<typeof Button> &
  (
    | PlayFromUrlProps
    | PlayFromBufferProps
    | PlayFromFetchableProps
    | PlayFromStoreProps
  )

const isUrlProps = (props: PlayButtonProps): props is PlayFromUrlProps =>
  'url' in props

const isFetchableProps = (
  props: PlayButtonProps
): props is PlayFromFetchableProps => 'fetchBuffer' in props

const isBufferProps = (props: PlayButtonProps): props is PlayFromBufferProps =>
  'buffer' in props

const PlayButton: FC<PlayButtonProps> = (props) => {
  const [isLoading, setIsLoading] = useState(false)

  const buffer = useMediaStore(
    useShallow((state) => state.media[props.soundId])
  )
  const addMedia = useMediaStore((state) => state.addMedia)

  const { playAudioBuffer, isAudioPlaying, currentAudioId, stopPlayback } =
    useAudioStore(
      useShallow((state) => ({
        playAudioBuffer: state.playAudioBuffer,
        isAudioPlaying: state.isPlaying,
        currentAudioId: state.currentAudioId,
        stopPlayback: state.stopPlayback,
      }))
    )

  const isPlaying = isAudioPlaying && currentAudioId === props.soundId

  const onPlayClick = useCallback(async () => {
    if (buffer) {
      playAudioBuffer(props.soundId, buffer, undefined, props.playAt)
      return
    }

    setIsLoading(true)

    let tmpBuffer: AudioBuffer | undefined
    if (isFetchableProps(props)) {
      tmpBuffer = await props.fetchBuffer()
    } else if (isUrlProps(props)) {
      tmpBuffer = await getAudioBufferFromAudioUrl(props.url)
    } else if (isBufferProps(props)) {
      tmpBuffer = props.buffer
    }

    if (tmpBuffer) {
      addMedia(props.soundId, tmpBuffer)
      playAudioBuffer(props.soundId, tmpBuffer, undefined, props.playAt)
    }

    setIsLoading(false)
  }, [buffer, playAudioBuffer, props, addMedia])

  return (
    <Button
      squared
      onClick={isPlaying ? stopPlayback : onPlayClick}
      pulse={isLoading}
    >
      <Icon name={isPlaying ? 'stop' : 'play'} />
    </Button>
  )
}

export default PlayButton
